import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wifi, WifiOff } from "lucide-react";
import { watchOnline } from "@/lib/sync/localStore";

/**
 * ConnectionStatus — small pulsing chip showing online/offline + last-sync timestamp.
 * Per Team Lead directive: never use "locked" or alarm language; just operational status.
 */
export default function ConnectionStatus({ lastSyncedAt }) {
    const [online, setOnline] = useState(true);
    useEffect(() => {
        const unsub = watchOnline(({ online: o }) => setOnline(o));
        return unsub;
    }, []);

    const since = lastSyncedAt ? timeAgo(lastSyncedAt) : "—";

    return (
        <div
            data-testid="connection-status"
            className="inline-flex items-center gap-2.5 font-mono-tight text-[10px] uppercase tracking-[0.22em]"
        >
            <motion.span
                aria-hidden="true"
                animate={{ opacity: online ? [0.55, 1, 0.55] : 0.5 }}
                transition={{ duration: 2.2, repeat: Infinity }}
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                    online ? "bg-tca-cyan shadow-[0_0_10px_rgba(0,219,255,0.7)]" : "bg-tca-ink-muted"
                }`}
            />
            {online ? (
                <>
                    <Wifi size={11} className="text-tca-cyan/85" strokeWidth={1.8} />
                    <span className="text-tca-cyan/85">Online · synced {since}</span>
                </>
            ) : (
                <>
                    <WifiOff size={11} className="text-tca-ink-muted" strokeWidth={1.8} />
                    <span className="text-tca-ink-muted">Offline · working locally</span>
                </>
            )}
        </div>
    );
}

function timeAgo(iso) {
    try {
        const t = new Date(iso).getTime();
        const s = Math.max(0, (Date.now() - t) / 1000);
        if (s < 30) return "just now";
        if (s < 90) return "1m ago";
        if (s < 3600) return `${Math.round(s / 60)}m ago`;
        if (s < 86400) return `${Math.round(s / 3600)}h ago`;
        return `${Math.round(s / 86400)}d ago`;
    } catch {
        return "—";
    }
}
